import { Button, Message, Progress } from '@arco-design/web-react';
import React, { useCallback, useMemo, useState } from 'react';
import { useExcelJS } from 'react-use-exceljs';
import { EXPORT_CONFIG, ExportModalProps } from './types';

export const ExportModal: React.FC<ExportModalProps> = ({
  params = {},
  title,
  columns,
  resolver,
  selection,
  pageSize = EXPORT_CONFIG.DEFAULT_PAGE_SIZE,
  showProgress = true,
  buttonText = '数据导出',
  buttonType = 'primary',
  buttonStatus = 'default',
}) => {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [currentCount, setCurrentCount] = useState(0);

  // 使用 useMemo 优化 excel 配置
  const excel = useExcelJS({
    filename: `${title}.xlsx`,
    worksheets: [
      {
        name: 'Sheet 1',
        columns: columns as any,
      },
    ],
  });

  // 重试机制
  const retryOperation = useCallback(
    async <T,>(
      operation: () => Promise<T>,
      retryCount = EXPORT_CONFIG.MAX_RETRY_COUNT
    ): Promise<T> => {
      try {
        return await operation();
      } catch (error) {
        if (retryCount > 0) {
          console.warn(`操作失败，${retryCount}秒后重试:`, error);
          await new Promise((resolve) => setTimeout(resolve, EXPORT_CONFIG.RETRY_DELAY));
          return retryOperation(operation, retryCount - 1);
        }
        throw error;
      }
    },
    []
  );

  // 优化的数据获取函数
  const fetchAllData = useCallback(async (): Promise<any[]> => {
    const allItems: any[] = [];
    let pageIndex = 1;
    let hasMore = true;
    let totalItems = 0;

    // 获取总数
    try {
      const firstPage = await retryOperation(() =>
        selection({
          ...params,
          pageSize: 1,
          pageIndex: 1,
        })
      );
      totalItems = firstPage.data.total;
      setTotalCount(totalItems);
    } catch (error) {
      console.error('获取数据总数失败:', error);
      Message.error('获取数据总数失败，请重试');
      throw error;
    }

    // 分批获取数据
    while (hasMore) {
      try {
        const res = await retryOperation(() =>
          selection({
            ...params,
            pageSize,
            pageIndex,
          })
        );

        if (res.data.items.length === 0) {
          hasMore = false;
        } else {
          allItems.push(...res.data.items);
          setCurrentCount(allItems.length);

          // 更新进度
          if (showProgress && totalItems > 0) {
            const progressPercent = Math.min((allItems.length / totalItems) * 100, 100);
            setProgress(progressPercent);
          }

          pageIndex += 1;

          // 防止无限循环
          if (pageIndex > 1000) {
            console.warn('导出页数过多，可能存在无限循环');
            break;
          }
        }
      } catch (error) {
        console.error(`获取第${pageIndex}页数据失败:`, error);
        Message.error(`获取第${pageIndex}页数据失败，请重试`);
        throw error;
      }
    }

    return allItems;
  }, [params, selection, pageSize, retryOperation, showProgress]);

  // 处理导出
  const handleExport = useCallback(async () => {
    if (loading) return;

    setLoading(true);
    setProgress(0);
    setCurrentCount(0);
    setTotalCount(0);

    try {
      const items = await fetchAllData();

      if (items.length === 0) {
        Message.warning('没有数据可导出');
        return;
      }

      // 转换数据
      const transformedData = items.map(resolver);

      // 下载文件
      await excel.download(transformedData);

      Message.success({
        content: `导出成功! 共导出 ${items.length} 条记录`,
        duration: 3000,
      });
    } catch (error) {
      console.error('导出失败:', error);
      Message.error({
        content: error instanceof Error ? error.message : '导出失败，请重试',
        duration: 5000,
      });
    } finally {
      setLoading(false);
      setProgress(0);
      setCurrentCount(0);
      setTotalCount(0);
    }
  }, [loading, fetchAllData, resolver, excel]);

  // 渲染进度条
  const renderProgress = useMemo(() => {
    if (!showProgress || !loading) return null;

    return (
      <div style={{ marginTop: 8 }}>
        <Progress
          percent={progress}
          status={progress === 100 ? 'success' : 'normal'}
          showText
          formatText={(percent) => `${Math.round(percent)}% (${currentCount}/${totalCount})`}
        />
      </div>
    );
  }, [showProgress, loading, progress, currentCount, totalCount]);

  return (
    <div>
      <Button
        onClick={handleExport}
        loading={loading}
        type={buttonType}
        status={buttonStatus}
        disabled={loading}
      >
        {buttonText}
      </Button>
      {renderProgress}
    </div>
  );
};
